import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Inbox, Ship, Plus, Trash2, X, Loader2, RefreshCw, Car as CarIcon, FileText, BarChart3, ExternalLink, Bot } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { Logo } from "../components/Brand";
import { ContentEditor } from "../components/admin/ContentEditor";
import { AnalyticsPanel } from "../components/admin/AnalyticsPanel";
import { PagesManager } from "../components/admin/PagesManager";
import { ImageUploader } from "../components/admin/ImageUploader";
import { MultiImageUploader } from "../components/admin/MultiImageUploader";
import { AssistantLeadsPanel } from "../components/admin/AssistantLeadsPanel";

const STATUS_OPTIONS = ["Booked", "At Port", "In Transit", "Delayed", "Delivered"];
const QUOTE_STATUS = ["new", "contacted", "quoted", "won", "lost"];
const field = "w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-navy outline-none focus:border-navy focus:ring-2 focus:ring-navy/20";

const emptyShipment = {
  tracking_id: "", customer_name: "", origin: "", destination: "Tema, Ghana",
  cargo_description: "", status: "Booked", current_location: "", estimated_delivery: "", events: [],
};

const emptyCar = {
  title: "", make: "", model: "", year: "", price: "", mileage: "", transmission: "",
  fuel_type: "", condition: "Foreign Used", body_type: "", image_url: "", description: "",
  featured: false, status: "available",
  images: [], power: "", engine: "", exterior_color: "", doors: "", seats: "", vin: "",
  first_registration: "", features: [],
};

export default function AdminDashboard() {
  const { user, ready, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("quotes");
  const [quotes, setQuotes] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyShipment);
  const [saving, setSaving] = useState(false);
  const [showCarForm, setShowCarForm] = useState(false);
  const [editingCar, setEditingCar] = useState(null);
  const [carForm, setCarForm] = useState(emptyCar);
  const [savingCar, setSavingCar] = useState(false);
  const [aiUnread, setAiUnread] = useState(0);

  useEffect(() => {
    if (ready && !user) navigate("/admin/login");
  }, [ready, user, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [q, s, c] = await Promise.all([
        api.get("/admin/quotes"),
        api.get("/admin/shipments"),
        api.get("/admin/cars"),
      ]);
      setQuotes(q.data);
      setShipments(s.data);
      setCars(c.data);
      api.get("/admin/assistant/unread-count").then(({ data }) => setAiUnread(data.count)).catch(() => {});
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (user) load(); }, [user, load]);

  const selectTab = useCallback((id) => {
    setTab(id);
    if (id === "assistant" && aiUnread > 0) {
      api.post("/admin/assistant/mark-read").then(() => setAiUnread(0)).catch(() => {});
    }
  }, [aiUnread]);

  const updateQuoteStatus = async (id, status) => {
    try {
      await api.patch(`/admin/quotes/${id}?status=${status}`);
      setQuotes((qs) => qs.map((q) => (q.id === id ? { ...q, status } : q)));
      toast.success("Quote updated");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const openNew = () => { setEditing(null); setForm(emptyShipment); setShowForm(true); };
  const openEdit = (s) => { setEditing(s.id); setForm({ ...emptyShipment, ...s }); setShowForm(true); };

  const saveShipment = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/shipments/${editing}`, form);
        toast.success("Shipment updated");
      } else {
        await api.post("/admin/shipments", form);
        toast.success("Shipment created");
      }
      setShowForm(false);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const removeShipment = async (id) => {
    if (!window.confirm("Delete this shipment?")) return;
    try { await api.delete(`/admin/shipments/${id}`); setShipments((s) => s.filter((x) => x.id !== id)); toast.success("Deleted"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const addEvent = () => setForm((f) => ({ ...f, events: [...f.events, { status: "", location: "", date: "", note: "" }] }));
  const updateEvent = (i, k, v) => setForm((f) => ({ ...f, events: f.events.map((e, idx) => idx === i ? { ...e, [k]: v } : e) }));
  const removeEvent = (i) => setForm((f) => ({ ...f, events: f.events.filter((_, idx) => idx !== i) }));

  const openNewCar = () => { setEditingCar(null); setCarForm(emptyCar); setShowCarForm(true); };
  const openEditCar = (c) => { setEditingCar(c.id); setCarForm({ ...emptyCar, ...c, year: c.year || "" }); setShowCarForm(true); };

  const saveCar = async (e) => {
    e.preventDefault();
    setSavingCar(true);
    try {
      const payload = {
        ...carForm,
        year: carForm.year ? Number(carForm.year) : null,
        features: (carForm.features || []).map((s) => s.trim()).filter(Boolean),
        images: (carForm.images || []).filter(Boolean),
      };
      if (editingCar) {
        await api.put(`/admin/cars/${editingCar}`, payload);
        toast.success("Vehicle updated");
      } else {
        await api.post("/admin/cars", payload);
        toast.success("Vehicle added");
      }
      setShowCarForm(false);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSavingCar(false); }
  };

  const removeCar = async (id) => {
    if (!window.confirm("Delete this vehicle?")) return;
    try { await api.delete(`/admin/cars/${id}`); setCars((cs) => cs.filter((x) => x.id !== id)); toast.success("Deleted"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  if (!ready || !user) {
    return <div className="flex min-h-screen items-center justify-center bg-[#0A2C66]"><Loader2 className="h-8 w-8 animate-spin text-white" /></div>;
  }

  const NAV = [
    { id: "quotes", label: "Quote Leads", icon: Inbox, count: quotes.length },
    { id: "assistant", label: "AI Assistant", icon: Bot, count: aiUnread || undefined },
    { id: "shipments", label: "Shipments", icon: Ship, count: shipments.length },
    { id: "cars", label: "Auto Sales", icon: CarIcon, count: cars.length },
    { id: "content", label: "Site Content", icon: FileText },
    { id: "pages", label: "Pages", icon: FileText },
    { id: "analytics", label: "Analytics", icon: BarChart3 },
  ];
  const TITLES = {
    quotes: { t: "Quote Leads", s: "Incoming quote requests from your website." },
    assistant: { t: "AI Assistant", s: "Leads captured by ROK Assistant and full chat conversations." },
    shipments: { t: "Shipments", s: "Create and manage shipment tracking." },
    cars: { t: "Auto Sales", s: "Manage the vehicles listed for sale." },
    content: { t: "Site Content", s: "Edit any section of the public website. Changes publish on save." },
    pages: { t: "Pages", s: "Create and manage standalone pages & subpages." },
    analytics: { t: "Analytics", s: "See how many people visit your website." },
  };
  const head = TITLES[tab] || TITLES.quotes;

  return (
    <div className="flex min-h-screen bg-gradient-to-b from-[#0a2c66] via-[#0d3a72] to-[#082353]" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col border-r border-black/5 bg-white lg:flex">
        <div className="flex items-center gap-2 px-5 py-5">
          <Logo size="sm" />
        </div>
        <nav className="flex-1 space-y-1 px-3 py-2">
          {NAV.map((n) => {
            const active = tab === n.id;
            return (
              <button
                key={n.id}
                onClick={() => selectTab(n.id)}
                data-testid={`tab-${n.id}`}
                className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition ${
                  active ? "bg-navy text-white shadow-sm" : "text-navy/70 hover:bg-navy-50 hover:text-navy"
                }`}
              >
                <n.icon size={18} />
                <span className="flex-1 text-left">{n.label}</span>
                {typeof n.count === "number" && (
                  <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${active ? "bg-white/20 text-white" : n.id === "assistant" ? "bg-brandred text-white" : "bg-gold/25 text-gold-700"}`}>{n.count}</span>
                )}
              </button>
            );
          })}
        </nav>
        <div className="space-y-1 border-t border-black/5 px-3 py-3">
          <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold text-navy/70 hover:bg-navy-50 hover:text-navy">
            <ExternalLink size={18} /> View website
          </a>
          <button onClick={() => { logout(); navigate("/admin/login"); }} data-testid="admin-logout-btn" className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold text-brandred hover:bg-red-50">
            <LogOut size={18} /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="min-w-0 flex-1">
        <div className="sticky top-0 z-20 border-b border-white/10 bg-[#0a2c66]/80 px-5 py-5 backdrop-blur sm:px-8">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="font-display text-2xl font-black text-white">{head.t}</h1>
              <p className="mt-1 text-sm text-white/60">{head.s}</p>
            </div>
            <div className="flex items-center gap-3">
              {["quotes", "shipments", "cars"].includes(tab) && (
                <button onClick={load} className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2.5 text-sm font-semibold text-white hover:bg-white/20"><RefreshCw size={15} /> Refresh</button>
              )}
              {tab === "shipments" && (
                <button onClick={openNew} data-testid="new-shipment-btn" className="inline-flex items-center gap-2 rounded-full bg-brandred px-5 py-2.5 text-sm font-bold text-white hover:bg-brandred-700"><Plus size={16} /> New Shipment</button>
              )}
              {tab === "cars" && (
                <button onClick={openNewCar} data-testid="new-car-btn" className="inline-flex items-center gap-2 rounded-full bg-brandred px-5 py-2.5 text-sm font-bold text-white hover:bg-brandred-700"><Plus size={16} /> New Vehicle</button>
              )}
              <span className="hidden text-sm text-white/60 sm:block">{user.email}</span>
              {/* mobile nav */}
              <select value={tab} onChange={(e) => selectTab(e.target.value)} className="rounded-full border border-white/20 bg-white px-3 py-2 text-sm font-semibold text-navy lg:hidden">
                {NAV.map((n) => <option key={n.id} value={n.id}>{n.label}</option>)}
              </select>
            </div>
          </div>
        </div>

        <div className="px-5 py-6 sm:px-8">

        {tab === "content" ? (
          <ContentEditor />
        ) : tab === "assistant" ? (
          <AssistantLeadsPanel />
        ) : tab === "pages" ? (
          <PagesManager />
        ) : tab === "analytics" ? (
          <AnalyticsPanel />
        ) : loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-navy" /></div>
        ) : tab === "quotes" ? (
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
            {quotes.length === 0 ? <p className="p-10 text-center text-sm text-muted-foreground">No quote requests yet.</p> : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="bg-navy-50 text-xs uppercase tracking-wide text-navy">
                    <tr>
                      <th className="px-4 py-3">Contact</th><th className="px-4 py-3">Route</th>
                      <th className="px-4 py-3">Shipment</th><th className="px-4 py-3">Date</th><th className="px-4 py-3">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {quotes.map((q) => (
                      <tr key={q.id} data-testid={`quote-row-${q.id}`} className="align-top hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <p className="font-bold text-navy">{q.name}</p>
                          <p className="text-xs text-muted-foreground">{q.phone}</p>
                          <p className="text-xs text-muted-foreground">{q.email}</p>
                        </td>
                        <td className="px-4 py-3 text-navy">{q.shipping_from} → {q.shipping_to}</td>
                        <td className="px-4 py-3 text-xs text-muted-foreground">
                          {q.vehicle_type && <p>🚗 {q.vehicle_type}</p>}
                          {q.cargo_type && <p>📦 {q.cargo_type}</p>}
                          {q.notes && <p className="mt-1 italic">"{q.notes}"</p>}
                        </td>
                        <td className="px-4 py-3 text-xs text-muted-foreground">{q.preferred_date || "—"}</td>
                        <td className="px-4 py-3">
                          <select value={q.status} onChange={(e) => updateQuoteStatus(q.id, e.target.value)} className="rounded-lg border border-gray-200 px-2 py-1 text-xs font-semibold capitalize text-navy">
                            {QUOTE_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : tab === "shipments" ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {shipments.length === 0 ? <p className="col-span-full rounded-xl border border-gray-200 bg-white p-10 text-center text-sm text-muted-foreground">No shipments yet. Create one to enable tracking.</p> :
              shipments.map((s) => (
                <div key={s.id} data-testid={`shipment-card-${s.id}`} className="rounded-xl border border-gray-200 bg-white p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-display text-lg font-black text-navy">{s.tracking_id}</p>
                      <p className="text-sm text-muted-foreground">{s.customer_name}</p>
                    </div>
                    <span className="rounded-full bg-navy-50 px-2.5 py-1 text-xs font-bold text-navy">{s.status}</span>
                  </div>
                  <p className="mt-3 text-sm text-navy">{s.origin} → {s.destination}</p>
                  {s.current_location && <p className="text-xs text-muted-foreground">Now: {s.current_location}</p>}
                  {s.estimated_delivery && <p className="text-xs text-muted-foreground">ETA: {s.estimated_delivery}</p>}
                  <div className="mt-4 flex gap-2">
                    <button onClick={() => openEdit(s)} data-testid={`edit-shipment-${s.id}`} className="flex-1 rounded-lg border border-gray-200 py-2 text-sm font-semibold text-navy hover:bg-navy-50">Edit</button>
                    <button onClick={() => removeShipment(s.id)} data-testid={`delete-shipment-${s.id}`} className="rounded-lg border border-red-200 px-3 py-2 text-brandred hover:bg-red-50"><Trash2 size={16} /></button>
                  </div>
                </div>
              ))}
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {cars.length === 0 ? <p className="col-span-full rounded-xl border border-gray-200 bg-white p-10 text-center text-sm text-muted-foreground">No vehicles yet. Add one to display it in the Auto Sales section.</p> :
              cars.map((c) => (
                <div key={c.id} data-testid={`admin-car-card-${c.id}`} className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                  <div className="relative h-40">
                    <img src={c.image_url} alt={c.title} className={`h-full w-full object-cover ${c.status === "sold" ? "grayscale" : ""}`} />
                    <span className="absolute right-2 top-2 rounded-lg bg-navy px-2 py-1 text-xs font-black text-white">{c.price}</span>
                    {c.featured && <span className="absolute left-2 top-2 rounded-full bg-gold px-2 py-0.5 text-xs font-bold text-navy">Featured</span>}
                    {c.status === "sold" && <span className="absolute bottom-2 left-2 rounded bg-brandred px-2 py-0.5 text-xs font-bold text-white">SOLD</span>}
                  </div>
                  <div className="p-4">
                    <p className="font-display font-bold text-navy">{c.title}</p>
                    <p className="text-xs text-muted-foreground">{[c.mileage, c.transmission, c.fuel_type].filter(Boolean).join(" · ")}</p>
                    <div className="mt-3 flex gap-2">
                      <button onClick={() => openEditCar(c)} data-testid={`edit-car-${c.id}`} className="flex-1 rounded-lg border border-gray-200 py-2 text-sm font-semibold text-navy hover:bg-navy-50">Edit</button>
                      <button onClick={() => removeCar(c.id)} data-testid={`delete-car-${c.id}`} className="rounded-lg border border-red-200 px-3 py-2 text-brandred hover:bg-red-50"><Trash2 size={16} /></button>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        )}
        </div>
      </main>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 py-10">
          <form onSubmit={saveShipment} data-testid="shipment-form" className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-xl font-black text-navy">{editing ? "Edit" : "New"} Shipment</h2>
              <button type="button" onClick={() => setShowForm(false)}><X className="text-gray-400" /></button>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-semibold text-navy">Tracking ID {editing ? "" : "(auto if blank)"}</label>
                <input disabled={!!editing} value={form.tracking_id} onChange={(e) => setForm({ ...form, tracking_id: e.target.value })} className={field} placeholder="ROK..." data-testid="ship-tracking-id" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-navy">Customer Name *</label>
                <input required value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className={field} data-testid="ship-customer" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-navy">Origin *</label>
                <input required value={form.origin} onChange={(e) => setForm({ ...form, origin: e.target.value })} className={field} data-testid="ship-origin" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-navy">Destination</label>
                <input value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })} className={field} />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-navy">Status</label>
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={field} data-testid="ship-status">
                  {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-navy">Current Location</label>
                <input value={form.current_location || ""} onChange={(e) => setForm({ ...form, current_location: e.target.value })} className={field} />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1 block text-xs font-semibold text-navy">Estimated Delivery</label>
                <input value={form.estimated_delivery || ""} onChange={(e) => setForm({ ...form, estimated_delivery: e.target.value })} className={field} placeholder="e.g. 15 July 2026" />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1 block text-xs font-semibold text-navy">Cargo Description</label>
                <input value={form.cargo_description || ""} onChange={(e) => setForm({ ...form, cargo_description: e.target.value })} className={field} />
              </div>
            </div>

            <div className="mt-4">
              <div className="flex items-center justify-between">
                <label className="text-xs font-bold uppercase text-navy">Tracking Events</label>
                <button type="button" onClick={addEvent} className="text-xs font-bold text-brandred">+ Add event</button>
              </div>
              <div className="mt-2 space-y-2">
                {form.events.map((ev, i) => (
                  <div key={i} className="grid grid-cols-2 gap-2 rounded-lg border border-gray-100 bg-gray-50 p-2">
                    <input placeholder="Status" value={ev.status} onChange={(e) => updateEvent(i, "status", e.target.value)} className={field} />
                    <input placeholder="Location" value={ev.location} onChange={(e) => updateEvent(i, "location", e.target.value)} className={field} />
                    <input placeholder="Date" value={ev.date} onChange={(e) => updateEvent(i, "date", e.target.value)} className={field} />
                    <div className="flex gap-1">
                      <input placeholder="Note" value={ev.note || ""} onChange={(e) => updateEvent(i, "note", e.target.value)} className={field} />
                      <button type="button" onClick={() => removeEvent(i)} className="rounded-lg border border-red-200 px-2 text-brandred"><X size={14} /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <button type="submit" disabled={saving} data-testid="save-shipment-btn" className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-brandred px-6 py-3 text-sm font-bold text-white hover:bg-brandred-700 disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {editing ? "Save Changes" : "Create Shipment"}
            </button>
          </form>
        </div>
      )}

      {showCarForm && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 py-10">
          <form onSubmit={saveCar} data-testid="car-form" className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-xl font-black text-navy">{editingCar ? "Edit" : "New"} Vehicle</h2>
              <button type="button" onClick={() => setShowCarForm(false)}><X className="text-gray-400" /></button>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className="mb-1 block text-xs font-semibold text-navy">Title *</label>
                <input required value={carForm.title} onChange={(e) => setCarForm({ ...carForm, title: e.target.value })} className={field} placeholder="2021 Mercedes-Benz G-Class SUV" data-testid="car-title" />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1 block text-xs font-semibold text-navy">Vehicle Image *</label>
                <ImageUploader value={carForm.image_url} onChange={(v) => setCarForm({ ...carForm, image_url: v })} testid="car-image" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-navy">Price *</label>
                <input required value={carForm.price} onChange={(e) => setCarForm({ ...carForm, price: e.target.value })} className={field} placeholder="$25,000" data-testid="car-price" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-navy">Year</label>
                <input type="number" value={carForm.year} onChange={(e) => setCarForm({ ...carForm, year: e.target.value })} className={field} placeholder="2021" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-navy">Mileage</label>
                <input value={carForm.mileage} onChange={(e) => setCarForm({ ...carForm, mileage: e.target.value })} className={field} placeholder="24,000 mi" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-navy">Body Type</label>
                <input value={carForm.body_type} onChange={(e) => setCarForm({ ...carForm, body_type: e.target.value })} className={field} placeholder="SUV" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-navy">Transmission</label>
                <input value={carForm.transmission} onChange={(e) => setCarForm({ ...carForm, transmission: e.target.value })} className={field} placeholder="Automatic" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-navy">Fuel Type</label>
                <input value={carForm.fuel_type} onChange={(e) => setCarForm({ ...carForm, fuel_type: e.target.value })} className={field} placeholder="Petrol" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-navy">Power</label>
                <input value={carForm.power} onChange={(e) => setCarForm({ ...carForm, power: e.target.value })} className={field} placeholder="158 hp" data-testid="car-power" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-navy">Engine</label>
                <input value={carForm.engine} onChange={(e) => setCarForm({ ...carForm, engine: e.target.value })} className={field} placeholder="1.3L Turbo" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-navy">Exterior Color</label>
                <input value={carForm.exterior_color} onChange={(e) => setCarForm({ ...carForm, exterior_color: e.target.value })} className={field} placeholder="Black" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-navy">First Registration</label>
                <input value={carForm.first_registration} onChange={(e) => setCarForm({ ...carForm, first_registration: e.target.value })} className={field} placeholder="07/2023" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-navy">Doors</label>
                <input value={carForm.doors} onChange={(e) => setCarForm({ ...carForm, doors: e.target.value })} className={field} placeholder="5" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-navy">Seats</label>
                <input value={carForm.seats} onChange={(e) => setCarForm({ ...carForm, seats: e.target.value })} className={field} placeholder="5" />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1 block text-xs font-semibold text-navy">VIN</label>
                <input value={carForm.vin} onChange={(e) => setCarForm({ ...carForm, vin: e.target.value })} className={field} placeholder="Vehicle Identification Number" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-navy">Condition</label>
                <input value={carForm.condition} onChange={(e) => setCarForm({ ...carForm, condition: e.target.value })} className={field} placeholder="Foreign Used" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-navy">Status</label>
                <select value={carForm.status} onChange={(e) => setCarForm({ ...carForm, status: e.target.value })} className={field} data-testid="car-status">
                  <option value="available">Available</option>
                  <option value="sold">Sold</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1 block text-xs font-semibold text-navy">Description</label>
                <textarea rows={2} value={carForm.description} onChange={(e) => setCarForm({ ...carForm, description: e.target.value })} className={field} />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1 block text-xs font-semibold text-navy">Extra Gallery Photos</label>
                <MultiImageUploader value={carForm.images || []} onChange={(imgs) => setCarForm({ ...carForm, images: imgs })} testid="car-gallery" />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1 block text-xs font-semibold text-navy">Features & Equipment (one per line)</label>
                <textarea rows={4} value={(carForm.features || []).join("\n")} onChange={(e) => setCarForm({ ...carForm, features: e.target.value.split("\n") })} className={field} placeholder={"Apple CarPlay\nReversing camera\nLeather seats\nLED headlights"} data-testid="car-features" />
              </div>
              <label className="flex items-center gap-2 text-sm font-semibold text-navy sm:col-span-2">
                <input type="checkbox" checked={carForm.featured} onChange={(e) => setCarForm({ ...carForm, featured: e.target.checked })} data-testid="car-featured" /> Feature this vehicle
              </label>
            </div>

            <button type="submit" disabled={savingCar} data-testid="save-car-btn" className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-brandred px-6 py-3 text-sm font-bold text-white hover:bg-brandred-700 disabled:opacity-60">
              {savingCar ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {editingCar ? "Save Changes" : "Add Vehicle"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
